import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

import { SplitAuthLayout } from "../components/SplitAuthLayout";
import { useAuth } from "../lib/auth";

export function Login() {
  const { issueLink, state } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (state.status === "authenticated") {
    void navigate("/engagements", { replace: true });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await issueLink(email);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "request failed");
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <SplitAuthLayout>
        <Step value="Step 2 of 2" />
        <h3 style={styles.heading}>Check your inbox.</h3>
        <p style={styles.sub}>
          If <code style={styles.code}>{email}</code> is an operator account,
          a one-time link is on its way. The link expires in 15 minutes.
        </p>
        <div style={styles.confirmBox}>
          <span style={styles.confirmIcon}>
            <Check size={13} strokeWidth={2.25} />
          </span>
          <div>
            <div style={styles.confirmLabel}>Link issued</div>
            <div style={styles.confirmHint}>Awaiting magic-link verification</div>
          </div>
        </div>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout>
      <Step value="Step 1 of 2" />
      <h3 style={styles.heading}>Sign in.</h3>
      <p style={styles.sub}>
        Enter your operator email. A one-time sign-in link arrives in your
        inbox; it expires in 15 minutes.
      </p>

      <form onSubmit={onSubmit} style={styles.form}>
        <label style={styles.fieldLabel}>Operator email</label>
        <input
          type="email"
          required
          autoFocus
          placeholder="operator@bluelakeinfosec.io"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
        />
        <button type="submit" disabled={pending} style={styles.button}>
          <span>{pending ? "Requesting…" : "Send sign-in link"}</span>
          <ArrowRight size={15} strokeWidth={1.75} />
        </button>
        {error ? <p style={styles.error}>{error}</p> : null}
      </form>

      <div style={styles.divider} />

      <div style={styles.systemRow}>
        <SystemDot label="API healthy" tone="ok" />
        <SystemDot label="Link valid 15 min" tone="muted" />
      </div>
    </SplitAuthLayout>
  );
}

function Step({ value }: { value: string }) {
  return (
    <div
      style={{
        fontSize: 12.5,
        color: "var(--text-muted)",
        marginBottom: 14,
        fontWeight: 500,
      }}
    >
      {value}
    </div>
  );
}

function SystemDot({ label, tone }: { label: string; tone: "ok" | "muted" }) {
  const color = tone === "ok" ? "var(--success)" : "var(--text-muted)";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        fontSize: 12,
        color: "var(--text-muted)",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
        }}
      />
      {label}
    </span>
  );
}

const styles = {
  heading: {
    margin: "0 0 10px",
    fontFamily: "var(--font-display)",
    fontSize: 34,
    fontWeight: 500,
    letterSpacing: "-0.024em",
    color: "var(--text-primary)",
    lineHeight: 1.12,
  } as const,
  sub: {
    fontSize: 14.5,
    color: "var(--text-secondary)",
    margin: "0 0 30px",
    lineHeight: 1.6,
    maxWidth: 380,
    letterSpacing: "-0.005em",
  } as const,
  form: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  fieldLabel: {
    fontSize: 12.5,
    fontWeight: 500,
    color: "var(--text-secondary)",
    marginBottom: 2,
  } as const,
  input: {
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-input)",
    padding: "12px 14px",
    color: "var(--text-primary)",
    fontFamily: "var(--font-sans)",
    fontSize: 14,
  },
  button: {
    background: "var(--accent)",
    color: "#FFFFFF",
    border: "1px solid var(--accent)",
    borderRadius: "var(--radius-input)",
    padding: "12px 16px",
    fontWeight: 600,
    fontFamily: "var(--font-sans)",
    fontSize: 14,
    cursor: "pointer" as const,
    marginTop: 12,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  error: {
    color: "var(--error)",
    fontSize: 12.5,
    margin: "10px 0 0",
  } as const,
  divider: {
    height: 1,
    background: "var(--border-subtle)",
    margin: "30px 0 18px",
  } as const,
  systemRow: {
    display: "flex",
    gap: 22,
    flexWrap: "wrap" as const,
  },
  code: {
    fontFamily: "var(--font-mono)",
    background: "var(--bg-tertiary)",
    padding: "1px 6px",
    borderRadius: 4,
    color: "var(--accent)",
    fontSize: 13,
  } as const,
  confirmBox: {
    marginTop: 18,
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: 14,
    padding: "16px 18px",
    border: "1px solid var(--border-subtle)",
    borderLeft: "2px solid var(--success)",
    borderRadius: "var(--radius-card)",
    background: "var(--bg-card-hero)",
    alignItems: "center",
  } as const,
  confirmIcon: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "var(--success)",
    color: "#FFFFFF",
    display: "grid",
    placeItems: "center",
  } as const,
  confirmLabel: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "var(--text-primary)",
  } as const,
  confirmHint: {
    fontSize: 12.5,
    color: "var(--text-muted)",
    marginTop: 2,
  } as const,
};
